import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PageHeader from 'Utils/PageHeader';
import { Dropdown } from 'react-bootstrap';
import { getSecureDataLandscape, listMaskingJobs, listEncryptJobs, listDecryptJobs, readableJobError } from 'Secure/api';
import FetchContainer from 'Utils/FetchContainer';
import { getFetchHandler } from 'Secure/Utils';
import { MdSecurity } from 'react-icons/md';
import { BRAND, INK, STATUS, OPERATION } from 'Utils/styleTokens';
import AppContext from 'Utils/AppContext';
import ActivityChart30d from 'Utils/ActivityChart30d';
import { useActivityWindowDays, ActivityWindowPicker, windowLabel } from 'Utils/ActivityWindow';
import StatTile from 'Utils/StatTile';
import FunnelBar from 'Utils/FunnelBar';
import MaskingReports from './MaskingReports';

// Mockup 3a: protection funnel in the header, 30-day protection activity, KPI strip,
// then the ledger (job results) table.
// Failures are not a series of their own: a failed job stacks in its own family's bar and is
// called out in the day's drilldown, in neutral ink.
const ACTIVITY_SERIES = [
  { key: 'mask', label: 'Mask jobs', color: BRAND.violet },
  { key: 'encrypt', label: 'Encrypt jobs', color: BRAND.blue },
];

const fmt = (n) => Number(n || 0).toLocaleString();

function SecureDataLandscape() {
  const history = useHistory();
  const [securedDataLandscape, setSecuredDataLandscape] = useState({});
  const [error, setError] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [events, setEvents] = useState([]);
  // Shared trailing window (see Utils/ActivityWindow).
  const [windowDays, setWindowDays] = useActivityWindowDays();
  const [recordsMasked, setRecordsMasked] = useState({ total: 0, sources: 0 });
  const [jobErrors, setJobErrors] = useState(0);
  const [jobCount, setJobCount] = useState(0);
  const { connections } = useContext(AppContext);
  const {
    totalSources = 0,
    encryptedCount = 0,
    maskedCount = 0,
  } = securedDataLandscape;

  const fetchHandler = useMemo(
    () => getFetchHandler(setFetchingData, setError, setSecuredDataLandscape, getSecureDataLandscape),
    []
  );

  useEffect(() => {
    const fetch = async () => {
      await fetchHandler(connections);
    };
    fetch();
  }, [connections, fetchHandler]);

  useEffect(() => {
    const loadJobs = async () => {
      const results = await Promise.allSettled([
        listMaskingJobs({ page: 0, size: 200 }),
        listEncryptJobs({ page: 0, size: 200 }),
        listDecryptJobs({ page: 0, size: 200 }),
      ]);
      const [maskJobs, encJobs, decJobs] = results.map((r) =>
        r.status === 'fulfilled' && Array.isArray(r.value?.content) ? r.value.content : []
      );
      // Enriched so the activity chart's bar drilldown can list each run: source name,
      // job kind, status, a results link, and (on failure) a readable error.
      const nameOf = (j) => {
        const c = (connections || []).find((x) => x && String(x.id) === String(j.dataSourceId));
        return (c && c.name) || j.dataSourceName || (j.dataSourceId != null ? `#${j.dataSourceId}` : '—');
      };
      const mk = (j, key, kind, resultsPath) => ({
        ts: j.startedAt || j.finishedAt,
        key,
        id: j.id,
        kind,
        name: nameOf(j),
        status: j.status,
        to:
          (j.status === 'SUCCEEDED' || j.status === 'FAILED') && j.id != null
            ? `${resultsPath}/${encodeURIComponent(j.id)}`
            : undefined,
        error: j.status === 'FAILED' ? readableJobError(j) : undefined,
      });
      const eventList = [
        ...maskJobs.map((j) => mk(j, 'mask', 'Mask', '/secure/mask/results')),
        ...encJobs.map((j) => mk(j, 'encrypt', 'Encrypt', '/secure/encrypt/results')),
        ...decJobs.map((j) => mk(j, 'encrypt', 'Decrypt', '/secure/decrypt/results')),
      ];
      setEvents(eventList);

      const allJobs = [...maskJobs, ...encJobs, ...decJobs];
      setJobCount(allJobs.length);
      setJobErrors(allJobs.filter((j) => j.status === 'FAILED').length);

      // Records masked: latest SUCCEEDED masking job per source (jobs arrive newest-first).
      const latestSucceeded = {};
      maskJobs.forEach((j) => {
        if (j.status === 'SUCCEEDED' && j.dataSourceId != null && !latestSucceeded[j.dataSourceId]) {
          latestSucceeded[j.dataSourceId] = j;
        }
      });
      const totals = Object.values(latestSucceeded).map((j) => Number(j.recordsProcessed) || 0);
      setRecordsMasked({ total: totals.reduce((a, n) => a + n, 0), sources: totals.length });
    };
    loadJobs();
  }, [connections]);

  const securedCount = maskedCount + encryptedCount;
  const unsecuredCount = Math.max(0, totalSources - securedCount);

  return (
    <div>
      <FetchContainer error={error} fetchingData={fetchingData} absolute={true} fluid>
        <PageHeader
          header='Secure Data Landscape'
          icon={MdSecurity}
          tooltip='Protect sensitive data in your discovered data sources.'
          meta={
            <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
              {jobCount} secured job{jobCount === 1 ? '' : 's'}
              {jobErrors > 0 && <span style={{ color: STATUS.danger.text }}> · {jobErrors} errors</span>}
            </span>
          }
          primaryAction={
            <FunnelBar
              caption='Secured coverage:'
              trailing={`${securedCount} of ${totalSources} sources`}
              segments={[
                { label: 'Masked', value: maskedCount, color: BRAND.violet },
                { label: 'Encrypted', value: encryptedCount, color: BRAND.blue },
                { label: 'Unsecured', value: unsecuredCount, color: INK[200] },
              ]}
            />
          }
          actions={[
            <Dropdown.Item
              key='new-masking-job'
              data-testid='secure-action-new-masking-job'
              onClick={() => history.push('/sources/pick?action=mask')}
            >
              New Masking Job
            </Dropdown.Item>,
            <Dropdown.Item
              key='new-encryption-job'
              data-testid='secure-action-new-encryption-job'
              onClick={() => history.push('/sources/pick?action=encrypt')}
            >
              New Encryption Job
            </Dropdown.Item>,
          ]}
        />
        <div className='d-flex align-items-center mb-2'>
          <div className='ml-auto'>
            <ActivityWindowPicker days={windowDays} onChange={setWindowDays} />
          </div>
        </div>
        <ActivityChart30d
          title={`Protection activity — ${windowLabel(windowDays)}`}
          series={ACTIVITY_SERIES}
          events={events}
          days={windowDays}
          drilldown
        />
        <div className='mb-3' style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <StatTile
            dot={BRAND.violet}
            label='Sources secured'
            value={<span>{securedCount} <small>/ {totalSources}</small></span>}
            sub={`${maskedCount} masked · ${encryptedCount} encrypted`}
          />
          <StatTile
            dot={OPERATION.mask.color}
            label='Records masked'
            value={fmt(recordsMasked.total)}
            sub={`across ${recordsMasked.sources} source${recordsMasked.sources === 1 ? '' : 's'}`}
          />
          <StatTile
            dot={OPERATION.encrypt.color}
            label='Sources encrypted'
            value={fmt(encryptedCount)}
            sub='FPE encryption applied'
          />
          <StatTile
            dot={INK[300]}
            label='Synthesized'
            value='0'
            sub='not yet in use'
          />
        </div>
        <MaskingReports />
      </FetchContainer>
    </div>
  );
}

export default SecureDataLandscape;
